/**
 * CausalChainBlock - Display causal relationships as a flow
 */

import React from 'react'
import { CausalChainBlock as CausalChainBlockType } from '../../../../lib/cockpit/schemas'

interface CausalChainBlockProps {
  data: CausalChainBlockType['data']
  blockId: string
  compact?: boolean
}

const NODE_COLORS = {
  cause: 'bg-blue-100 border-blue-300 dark:bg-blue-900/30 dark:border-blue-700',
  effect: 'bg-green-100 border-green-300 dark:bg-green-900/30 dark:border-green-700',
  mechanism: 'bg-purple-100 border-purple-300 dark:bg-purple-900/30 dark:border-purple-700',
  condition: 'bg-orange-100 border-orange-300 dark:bg-orange-900/30 dark:border-orange-700',
}

interface CausalNode {
  id: string
  label: string
  type?: string
  description?: string
}

function CausalChainBlock({ data, blockId, compact }: CausalChainBlockProps) {
  // Build adjacency for simple horizontal layout
  const nodeById = new Map<string, CausalNode>(data.nodes.map((n: any) => [n.id, n as CausalNode]))
  
  // Find root nodes (no incoming edges)
  const hasIncoming = new Set(data.edges.map((e: any) => e.target))
  const rootNodes = data.nodes.filter((n: any) => !hasIncoming.has(n.id))

  // Simple linear traversal for now
  const traverseChain = (nodeId: string, visited = new Set<string>()): string[] => {
    if (visited.has(nodeId)) return []
    visited.add(nodeId)
    
    const outgoing = data.edges.filter((e: any) => e.source === nodeId)
    const result = [nodeId]
    
    for (const edge of outgoing) {
      result.push(...traverseChain(edge.target, visited))
    }
    
    return result
  }

  const orderedNodeIds = rootNodes.length > 0
    ? traverseChain(rootNodes[0].id)
    : data.nodes.map((n: any) => n.id)

  if (compact) {
    return (
      <div className="flex flex-wrap items-center gap-2">
        {orderedNodeIds.slice(0, 5).map((nodeId: string, index: number) => {
          const node = nodeById.get(nodeId)
          if (!node) return null
          return (
            <React.Fragment key={nodeId}>
              <span className="px-2 py-1 text-sm bg-gray-100 dark:bg-gray-800 rounded">
                {node.label}
              </span>
              {index < orderedNodeIds.slice(0, 5).length - 1 && (
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              )}
            </React.Fragment>
          )
        })}
      </div>
    )
  }

  const layout = data.layout ?? 'horizontal'

  return (
    <div className={`
      flex ${layout === 'vertical' ? 'flex-col' : 'flex-row flex-wrap'}
      items-center gap-4 p-4
    `}>
      {orderedNodeIds.map((nodeId: string, index: number) => {
        const node = nodeById.get(nodeId)
        if (!node) return null
        
        const colorClass = node.type ? NODE_COLORS[node.type as keyof typeof NODE_COLORS] : 'bg-gray-100 border-gray-300 dark:bg-gray-800 dark:border-gray-700'
        
        return (
          <React.Fragment key={nodeId}>
            {/* Node */}
            <div className={`
              px-4 py-3 rounded-lg border-2 
              ${colorClass}
              max-w-xs
            `}>
              <div className="font-medium text-gray-900 dark:text-white text-sm">
                {node.label}
              </div>
              {node.description && (
                <div className="mt-1 text-xs text-gray-600 dark:text-gray-400">
                  {node.description}
                </div>
              )}
              {node.type && (
                <div className="mt-2 text-xs text-gray-500 dark:text-gray-500 uppercase">
                  {node.type}
                </div>
              )}
            </div>
            
            {/* Arrow */}
            {index < orderedNodeIds.length - 1 && (
              <div className={layout === 'vertical' ? 'rotate-90' : ''}>
                <svg className="w-6 h-6 text-gray-400 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </div>
            )}
          </React.Fragment>
        )
      })}
    </div>
  )
}

export default CausalChainBlock



